import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { ChangeEvent, memo, useMemo, useState } from "react";
import { useField } from "react-final-form";
import { FilledTextArea } from "../../../../components/FilledTextArea";
import { formatSourceOfActiveDisplayLines } from "../../utils/configFieldDisplay";

const employmentIncome = "Employment income (salary/bonus)";
const perYear = "EUR per year";

type SourceOfActiveItem = {
  value?: string;
  label?: string;
  parent?: string;
  name?: string;
};

const sourceOfActiveItems: SourceOfActiveItem[] = [
  {
    name: `${employmentIncome} ${perYear}`,
  },
  {
    value: `${employmentIncome} 0 – 50'000 ${perYear}`,
    label: "0 – 50'000",
    parent: `${employmentIncome} ${perYear}`,
  },
  {
    value: `${employmentIncome} 50'000 - 100'000 ${perYear}`,
    label: "50'000 - 100'000",
    parent: `${employmentIncome} ${perYear}`,
  },
  {
    value: `${employmentIncome} 100'000 - 200'000 ${perYear}`,
    label: "100'000 - 200'000",
    parent: `${employmentIncome} ${perYear}`,
  },
  {
    value: `${employmentIncome} 200'000 - 500'000 ${perYear}`,
    label: "200'000 - 500'000",
    parent: `${employmentIncome} ${perYear}`,
  },
  {
    value: `${employmentIncome} 500'000 – 1'000'000 ${perYear}`,
    label: "500'000 – 1'000'000",
    parent: `${employmentIncome} ${perYear}`,
  },
  {
    value: `${employmentIncome} over 1'000'000 ${perYear}`,
    label: "Over 1'000'000",
    parent: `${employmentIncome} ${perYear}`,
  },
  {
    value: "Maturity life insurance policy",
    label: "Maturity life insurance policy",
  },
  {
    value: "Sale of investments/liquidation investment portfolio",
    label: "Sale of investments/liquidation investment portfolio",
  },
  {
    value: "Sale of property/assets",
    label: "Sale of property/assets",
  },
  {
    value: "Inheritance",
    label: "Inheritance",
  },
  {
    value: "Business income (profits)",
    label: "Business income (profits)",
  },
  {
    value: "Gift",
    label: "Gift",
  },
  {
    value: "Virtual asset mining",
    label: "Virtual asset mining",
  },
  {
    value: "Virtual asset trading",
    label: "Virtual asset trading",
  },
  {
    value: "Other",
    label: "Other (please specify)",
  },
];

const sourceValues = new Set(
  sourceOfActiveItems
    .map((item) => item.value)
    .filter((value): value is string => Boolean(value))
);

type Props = {
  name: string;
  label: string;
  value?: unknown;
  disabled?: boolean;
};

type SourceSelection = {
  values: string[];
  explanation: string;
};

function toArrayValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  if (typeof value === "string") {
    return value.split(",").filter(Boolean);
  }
  return [];
}

function parseSourceSelection(value: unknown): SourceSelection {
  if (typeof value !== "string" || value === "") {
    return { values: [], explanation: "" };
  }

  return value.split(", ").reduce<SourceSelection>(
    (result, item) => {
      const trimmed = item.trim();
      if (!trimmed) return result;
      if (trimmed.startsWith("Other:")) {
        result.values.push("Other");
        result.explanation = trimmed.slice("Other:".length).trim();
        return result;
      }
      result.values.push(trimmed);
      return result;
    },
    { values: [], explanation: "" }
  );
}

function normalizeSelectedValues(
  values: string[],
  previousValues: string[]
): string[] {
  let nextValues = values;
  const newValue = values[values.length - 1];

  if (
    newValue?.includes(employmentIncome) &&
    previousValues.length < values.length
  ) {
    nextValues = [
      ...previousValues.filter((item) => !item.includes(employmentIncome)),
      newValue,
    ];
  }

  if (nextValues.includes("Other")) {
    nextValues = [
      ...nextValues.filter((item) => item !== "Other"),
      "Other",
    ];
  }

  return nextValues;
}

function composeSourceValue(values: string[], explanation: string): string {
  return values
    .map((value) => (value === "Other" ? `Other: ${explanation}` : value))
    .join(", ");
}

function renderSelectedValue(values: string[]): string {
  return values
    .map((value) => {
      const item = sourceOfActiveItems.find((option) => option.value === value);
      return item?.parent ? item.value : item?.label ?? value;
    })
    .join(", ");
}

function isStructuredSourceValue(value: unknown): boolean {
  if (typeof value !== "string" || value === "") return false;

  const items = value.split(", ").map((item) => item.trim()).filter(Boolean);
  return items.every(
    (item) => item.startsWith("Other:") || sourceValues.has(item)
  );
}

function useSourceOfActive(name: string) {
  const { input } = useField<string | undefined>(name, {
    subscription: { value: true },
  });
  const { input: explanationInput } = useField<string | undefined>(
    `del_${name}`,
    { subscription: { value: true } }
  );
  const parsedSelection = useMemo(
    () => parseSourceSelection(input.value),
    [input.value]
  );
  const selectedValues = parsedSelection.values;
  const explanation =
    typeof explanationInput.value === "string"
      ? explanationInput.value
      : parsedSelection.explanation;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const values = toArrayValue(event.target.value);
    const nextValues = normalizeSelectedValues(values, selectedValues);

    input.onChange(composeSourceValue(nextValues, explanation));
    input.onBlur();

    if (!nextValues.includes("Other") && explanation) {
      explanationInput.onChange("");
      explanationInput.onBlur();
    }
  };

  const handleExplanationChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextExplanation = event.target.value;
    explanationInput.onChange(nextExplanation);
    input.onChange(composeSourceValue(selectedValues, nextExplanation));
  };

  return {
    fieldName: input.name,
    explanationFieldName: explanationInput.name,
    selectedValues,
    explanation,
    showOther: selectedValues.includes("Other"),
    handleChange,
    handleExplanationChange,
    handleBlur: input.onBlur,
    handleFocus: input.onFocus,
    handleExplanationBlur: explanationInput.onBlur,
  };
}

export const SourceOfActiveField = memo((props: Props) => {
  const { name, label, value, disabled } = props;

  if (disabled) {
    const lines = formatSourceOfActiveDisplayLines(value);
    if (!lines.length || !isStructuredSourceValue(value)) {
      return (
        <FilledTextArea
          value={value == null ? "" : String(value)}
          name={name}
          label={label}
          inputProps={{ disabled: true }}
        />
      );
    }

    return (
      <Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {lines.map((line, index) => (
            <Typography
              key={`${line}-${index}`}
              variant="body2"
              data-testid={`${name}-source-line-${index}`}
            >
              {line}
            </Typography>
          ))}
        </Stack>
      </Box>
    );
  }

  return <EditableSourceOfActiveField name={name} label={label} />;
});

const EditableSourceOfActiveField = memo(
  (props: Pick<Props, "name" | "label">) => {
    const { name, label } = props;
    const source = useSourceOfActive(name);
    const labelId = `${name}-source-label`;

    return (
      <Box>
        <GroupedSourceSelect
          name={source.fieldName}
          label={label}
          labelId={labelId}
          values={source.selectedValues}
          onChange={source.handleChange}
          onBlur={source.handleBlur}
          onFocus={source.handleFocus}
        />
        {source.showOther && (
          <TextField
            fullWidth
            variant="filled"
            name={source.explanationFieldName}
            label="Please specify your selection: 'Other'"
            value={source.explanation}
            onChange={source.handleExplanationChange}
            onBlur={() => {
              source.handleBlur();
              source.handleExplanationBlur();
            }}
            sx={{ mt: 2 }}
            inputProps={{
              "data-testid": `${name}-source-other`,
            }}
          />
        )}
      </Box>
    );
  }
);

type GroupedSourceSelectProps = {
  name: string;
  label: string;
  labelId: string;
  values: string[];
  onChange: (event: SelectChangeEvent<string[]>) => void;
  onBlur: () => void;
  onFocus: () => void;
};

const GroupedSourceSelect = memo((props: GroupedSourceSelectProps) => {
  const { name, label, labelId, values, onChange, onBlur, onFocus } = props;
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  const toggleGroup = (groupName: string) => {
    setOpenGroups((prev) =>
      prev.includes(groupName)
        ? prev.filter((item) => item !== groupName)
        : [...prev, groupName]
    );
  };

  return (
    <FormControl variant="filled" fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        variant="filled"
        multiple
        labelId={labelId}
        label={label}
        name={name}
        value={values}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        renderValue={(selected) => renderSelectedValue(selected as string[])}
        inputProps={{
          "data-testid": `${name}-source-select`,
        }}
      >
        <Stack sx={{ alignItems: "flex-end", px: 1 }}>
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
            }}
          >
            Done
          </Button>
        </Stack>
        {sourceOfActiveItems.flatMap((item, index) => {
          if (item.name) {
            const isOpen = openGroups.includes(item.name);
            return [
              <Stack
                key={`group-${item.name}`}
                onClickCapture={(event) => {
                  event.stopPropagation();
                  toggleGroup(item.name || "");
                }}
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  px: 2,
                  py: 0.75,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    mr: 1,
                    transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  }}
                />
                <Typography>{item.name}</Typography>
              </Stack>,
            ];
          }

          const value = item.value || "";
          const parentIsClosed =
            item.parent && !openGroups.includes(item.parent);

          return [
            <MenuItem
              key={`${value}-${index}`}
              value={value}
              sx={{
                display: parentIsClosed ? "none" : undefined,
                pl: item.parent ? 4 : undefined,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <Checkbox size="small" checked={values.includes(value)} />
              <ListItemText primary={item.label} />
            </MenuItem>,
          ];
        })}
      </Select>
    </FormControl>
  );
});
